import { Link, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { dataset } from "../lib/dataset";
import { factionLinkSearch } from "../lib/faction-selection";
import { frameLinkSearch } from "../lib/frame";
import { VIEWS } from "../lib/views";
import { FactionPicker } from "./faction-picker";
import { UnitSearch } from "./unit-search";

// The active view reads as a pressed tab (TanStack sets aria-current="page"); idle views
// stay quiet but visibly clickable — the plain-text links read as captions, not nav.
const NAV_LINK_CLASS =
  "rounded border border-line px-2.5 py-1 font-mono text-[12.5px] uppercase tracking-[0.08em] text-ink-3 transition-colors hover:border-line-strong hover:text-ink-2 aria-[current=page]:border-line-strong aria-[current=page]:bg-raised aria-[current=page]:text-ink";

export function AppShell({ children }: { children: ReactNode }) {
  const { meta } = dataset;
  // Read loosely: the nav sits above any one route, and every route declares `faction`
  // while only the stat-bearing ones declare the frame keys.
  const search = useSearch({ strict: false }) as {
    faction?: string;
    tech?: string;
    corruption?: string;
  };
  // What the reader has chosen follows them across the nav, not just along unit-level links:
  // the Faction Selection is site-wide (ADR 0010) and a Tech Set built on /tech is only worth
  // building because it moves the numbers on /ships and /structures (ADR 0009). Per-view state
  // — facets, sort, the Compare Set — deliberately does NOT ride: Ships and Structures do not
  // share a facet vocabulary, and /tech has neither.
  const navSearch = { ...factionLinkSearch(search), ...frameLinkSearch(search) };
  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-line bg-surface-deep">
        {/* flex-wrap on both rows: below ~360px content width the clusters stack instead
            of forcing horizontal page scroll. */}
        <div className="mx-auto flex w-full max-w-[1440px] flex-wrap items-center justify-between gap-x-4 gap-y-1.5 px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <span className="whitespace-nowrap font-condensed text-[17px] font-semibold uppercase tracking-[0.16em] text-ink">
              Sins II Atlas
            </span>
            {/* Ships · Structures · Tech — the game's own pair of build windows, then the
                research trees (ADR 0008; CONTEXT.md avoids "Roster" as a nav label). */}
            <nav className="flex items-center gap-1.5">
              {VIEWS.map((view) => (
                <Link key={view.path} to={view.path} search={navSearch} className={NAV_LINK_CLASS}>
                  {view.navLabel}
                </Link>
              ))}
              {/* /tech declares no `corruption` key — the tree publishes no stat for that
                  axis to move — so the router drops it here and the Corruption control
                  starts fresh on the way back. */}
              <Link to="/tech" search={navSearch} className={NAV_LINK_CLASS}>
                Tech
              </Link>
            </nav>
          </div>
          {/* flex-1 + justify-end: the search trigger absorbs the row's leftover space
              while the cluster stays right-packed. */}
          <div className="flex flex-1 items-center justify-end gap-3">
            {/* The one place the reader says which empire they are playing (ADR 0010):
                site-wide, so it sits in the shell beside the search, not on a page. */}
            <FactionPicker />
            <UnitSearch />
          </div>
        </div>
      </header>
      {/* tabIndex -1: the programmatic focus target when a compare affordance unmounts
          itself (tray Clear, last column Remove) — never in the tab order. */}
      <main
        tabIndex={-1}
        className="mx-auto w-full max-w-[1440px] flex-1 px-4 py-6 outline-none sm:px-6"
      >
        {children}
      </main>
      <footer className="mx-auto w-full max-w-[1440px] px-4 pb-8 sm:px-6">
        {/* The version stamp lives here and nowhere else (user decision 2026-09-06): the
            header is for controls, and a build id is provenance, which is what a footer is
            for. It reads at every width, which the header's `sm:` stamp never did. */}
        <p className="border-t border-line-faint pt-4 text-[12.5px] text-muted">
          Unofficial reference built from the game's data files (game{" "}
          <span className="font-mono text-ink-3">{meta.gameVersion}</span>, build{" "}
          <span className="font-mono text-ink-3">{meta.steamBuildId}</span>). Sins of a Solar Empire
          II © Ironclad Games / Stardock Entertainment.
        </p>
      </footer>
    </div>
  );
}
